import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BalanceReportCustomRowFormDto,
  BalanceReportCustomRowFormInterface
} from '@zaoblako/shared/modules/finance/dtos/balance-report-custom-row/balance-report-custom-row-form.dto';
import type { BalanceReportRowTokenInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-custom-row/balance-report-row-token.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { BalanceReportService } from '@zaoblako/web/core/services/report/balance-report.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { MessagesModule } from 'primeng/messages';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { TreeModule } from 'primeng/tree';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-balance-custom-row-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonModule,
    InputTextComponent,
    JoinPipe,
    CardModule,
    MessagesModule,
    OverlayPanelModule,
    OrderByPipe,
    CheckboxModule,
    FormsModule,
    TreeModule
  ],
  templateUrl: './balance-custom-row-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BalanceCustomRowFormComponent
  extends BaseFormComponent<BalanceReportCustomRowFormInterface>
  implements OnInit {
  @ViewChild('expressionInput') public expressionInput: InputTextComponent;
  @ViewChild('opExpression') public opExpression: OverlayPanel;

  @Input() public readonly index: number;
  public form: BalanceReportCustomRowFormInterface = {
    expression: '',
    name: ''
  };

  public dto = BalanceReportCustomRowFormDto;
  public engine: 'class-transformer' | 'serde' = 'serde';
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public isShowOverlayPanel = false;
  public resultExpression = '';

  public customRows: TreeNode<BalanceReportRowTokenInterface>[] = [];

  public constructor(
    private readonly balanceReportService: BalanceReportService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.formStoreService.setLoading(true, this.index);
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.balanceReportService.getCustomRowTokens().subscribe({
      next: (customRows) => {
        const parseChildCustomRow = (rows: BalanceReportRowTokenInterface[]): TreeNode[] => {
          const result: TreeNode[] = [];
          for (let row of rows) {
            const node: TreeNode = {
              label: row.name,
              selectable: true,
              key: row.key,
              expanded: true,
              children: row.children?.length ? parseChildCustomRow(row.children) : [],
              data: row
            };
            result.push(node);
          }
          return result;
        };

        this.customRows = parseChildCustomRow(customRows);
        this.cdRef.markForCheck();
        if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
          this.formStoreService.setLoading(false, this.index);
        }
        this.parseExpression();
      }
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.balanceReportService.findOneCustomRow(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
      next: (item) => {
        this.form = item;
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
        this.parseExpression();
      }
    });
  }

  public saveItem() {
    if (this.checkValidate(this.form).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.balanceReportService
          .updateOneCustomRow(this.formStoreService.getState.forms[this.index]?.entityId, this.form)
          .subscribe({
            next: (item) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Фин.показатель "${this.form?.name}" обновлен`);
              this.formStoreService.saveNext(item, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.balanceReportService.createOneCustomRow(this.form).subscribe({
          next: (item) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Фин.показатель создан');
            this.formStoreService.saveNext(item, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteCustomRow() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить Фин.показатель "${this.form?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.balanceReportService
          .deleteOneCustomRow(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Фин.показатель "${this.form?.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public togglePercent() {
    if (this.form.measure === '%') {
      this.form.measure = '';
    } else {
      this.form.measure = '%';
    }
  }

  public toggleColor() {
    if (this.form.color === '#F4F4F5') {
      this.form.color = null;
    } else {
      this.form.color = '#F4F4F5';
    }
  }

  public selectCustomRow(customRow: TreeNode<BalanceReportRowTokenInterface>) {
    const selectionStart = (this.expressionInput?.input?.nativeElement as HTMLInputElement)?.selectionStart;
    if (!isNaN(selectionStart)) {
      if (!this.form.expression) {
        this.form.expression = '';
      }
      this.form.expression =
        this.form.expression.slice(0, selectionStart) + customRow.key + this.form.expression.slice(selectionStart);
      (this.expressionInput?.input?.nativeElement as HTMLInputElement)?.focus();
      setTimeout(() => {
        (this.expressionInput?.input?.nativeElement as HTMLInputElement)?.setSelectionRange(
          selectionStart + customRow.key?.length,
          selectionStart + customRow.key?.length
        );
      });
      this.parseExpression();
    }
  }

  public parseExpression() {
    const flattenArray = (rows: TreeNode<BalanceReportRowTokenInterface>[]) => {
      let result: TreeNode<BalanceReportRowTokenInterface>[] = [];

      rows.forEach((row) => {
        result.push(row);
        if (row.children) {
          result = result.concat(flattenArray(row.children));
        }
      });
      return result;
    };

    let expression = this.form.expression || '';
    const customRows = [...flattenArray(this.customRows)].sort((a, b) => (a.key?.length > b.key?.length ? -1 : 1));
    customRows.forEach((customRow) => {
      if (expression.includes(customRow.key)) {
        expression = expression.replace(customRow.key, `"${customRow.label}"`);
      }
    });
    this.resultExpression = expression;
  }

  public keydownEscInExpressionInput(event: KeyboardEvent) {
    if (this.isShowOverlayPanel) {
      this.opExpression.hide();
      event.preventDefault();
      event.stopPropagation();
    }
  }
}
